import { createSlice } from '@reduxjs/toolkit';
import { GetBuyer } from './thunks/get-buyer.thunk';
import { BuyersState } from './buyer.state';
import { GetBuyers } from './thunks/get-buyers.thunk';
import { GetBuyerNdaTermPreferences } from './thunks/get-nda-term-preferences';
import { SaveSelectedOpportunity } from './thunks/save-selected-opportunity.thunk';
import {
    ExternalTeamMemberRole,
    InternalTeamMemberRole,
    TeamMemberType,
} from '../../../containers/Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';

const defaultPageNumber = 1;
const defaultTotalItems = 1;
const defaultPageSize = 5;

const initialState: BuyersState = {
    buyersTable: {
        buyers: [],
        pageNumber: defaultPageNumber,
        totalItems: defaultTotalItems,
        pageSize: defaultPageSize,
    },
    targetBuyer: null,
    isLoading: false,
    status: 'succeeded',
    error: '',
};

const buyersSlice = createSlice({
    name: 'buyers',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(GetBuyer.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetBuyer.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetBuyer.fulfilled, (state, action) => {
                const { buyer } = action.payload;
                state.targetBuyer = buyer;
                setSucceededState(state);
            })
            .addCase(GetBuyers.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetBuyers.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetBuyers.fulfilled, (state, action) => {
                const { buyers, pageNumber, totalItems, pageSize } =
                    action.payload;
                state.buyersTable.buyers = buyers;
                state.buyersTable.pageNumber = pageNumber;
                state.buyersTable.totalItems = totalItems;
                state.buyersTable.pageSize = pageSize;
                setSucceededState(state);
            })
            .addCase(GetBuyerNdaTermPreferences.pending, (state) => {
                setPendingState(state);
            })
            .addCase(GetBuyerNdaTermPreferences.rejected, (state) => {
                setRejectedState(state);
            })
            .addCase(GetBuyerNdaTermPreferences.fulfilled, (state, action) => {
                const { ndaTermPreferences } = action.payload;
                if (state.targetBuyer == null) {
                    state.targetBuyer = {
                        id: 0,
                        buyerName: '',
                        corporateAffiliationName: '',
                        activeEngagementsCount: 0,
                        userId: 0,
                        profile: {
                            website: '',
                            companyName: '',
                            companyDescription: '',
                            stateAcquisitionRegion: [],
                            stateAcquisitionState: [],
                            geographyMarket: [],
                            targetPracticeSizeRevenueMin: 0,
                            targetPracticeSizeRevenueMax: 0,
                            targetPracticeSizeRevenueSweetspot: 0,
                            targetPracticeSizeFteMin: 0,
                            targetPracticeSizeFteMax: 0,
                            targetPracticeSizeFteSweetspot: 0,
                            logoUrl: '',
                            growthMapUrl: '',
                            uploads: [],
                            legalDocumentInputs: null,
                            currentNdaTermPreferences: null,
                            ndaTermPreferencesSnapshots: ndaTermPreferences,
                            practiceFocuses: [],
                        },
                        teamMemberRole: {
                            teamMemberType: TeamMemberType.External,
                            internalTeamMemberRole:
                                InternalTeamMemberRole.Analyst,
                            externalTeamMemberRole: ExternalTeamMemberRole.Qoe,
                            isPrimaryBuyer: false,
                        },
                        userFullName: '',
                        selectedOpportunityId: 0,
                    };
                } else {
                    state.targetBuyer.profile.ndaTermPreferencesSnapshots =
                        ndaTermPreferences;
                }
                setSucceededState(state);
            })
            .addCase(SaveSelectedOpportunity.pending, (state) =>
                setPendingState(state)
            )
            .addCase(SaveSelectedOpportunity.rejected, (state) =>
                setRejectedState(state)
            )
            .addCase(SaveSelectedOpportunity.fulfilled, (state, action) => {
                const selectedOpportunityId = action.payload as number;
                state.targetBuyer.selectedOpportunityId = selectedOpportunityId;
            });
    },
});

const setRejectedState = (state: any) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = state;
};

const setPendingState = (state: any) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: any) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};

export const {} = buyersSlice.actions;
export default buyersSlice.reducer;
